import React, { useRef, useState, useEffect, useContext } from 'react'
import { HiddenInput, RemoveButton, UploadArea, UploadContainer, UploadContent, UploadIcon, UploadText, FilePreviewContainer, FilePreviewImage, FilePreviewInfo, FullScreenModal, FullScreenImage, CloseButton } from './style'
import { FileUp, X, ImageIcon, Loader2 } from 'lucide-react'
import { CertificateType } from '../../../pages/ClientPanel/Certificates/CreateCertificate'
import ApiService from '../../../services/ApiService'
import AttachmentApiService from '../../../services/AttachmentApiService'
import { useOutletContext } from 'react-router-dom'
import { CertificateOutletType } from '../../../pages/ClientPanel/Certificates'
import AttachmentSourceModal from '../../AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../AttachmentGalleryModal'
import { FeedbackContext } from '../../../contexts/FeedbackContext'

interface SupplierFileComponentProps {
  certificate: CertificateType
  setCertificate: React.Dispatch<React.SetStateAction<CertificateType>>
}

interface OCRResponse {
  id: number
  field: string
  value: string
}

const SupplierFileComponent = ({ certificate, setCertificate }: SupplierFileComponentProps) => {
  const [ isDragActive, setIsDragActive ] = useState(false)
  const [ isFullScreenOpen, setIsFullScreenOpen ] = useState(false)
  const [ isSourceModalOpen, setIsSourceModalOpen ] = useState(false)
  const [ isGalleryModalOpen, setIsGalleryModalOpen ] = useState(false)
  const [ displayUrl, setDisplayUrl ] = useState<string>('')
  const [ imageError, setImageError ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setLoading } = useOutletContext<CertificateOutletType>()
  const { setFeedback } = useContext(FeedbackContext)

  const sendFileToOCR = (file: File) => {
    setLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    
    console.log('Enviando arquivo para OCR:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    })
    
    ApiService.ocr.process(formData)
    .then(response => {
      console.log('Resposta do OCR:', response.data)
      setCertificate(prev => ({
        ...prev,
        columnsInStructureWithValues: prev.columnsInStructureWithValues.map(column => {
          const ocrResponse:OCRResponse[] = response.data
          return {
            ...column,
            value: ocrResponse.find(ocr => ocr.id === column.id)?.value || ''
          }
        })
      }))
    })
    .catch(error => {
      console.error('Erro detalhado do OCR:', error)
      console.error('Status:', error.response?.status)
      console.error('Data:', error.response?.data)
      console.error('Headers:', error.response?.headers)
      
      let errorMessage = 'A imagem não foi reconhecida como um certificado de fornecedor.'
      
      if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message
        if (typeof backendMessage === 'string' && backendMessage.trim()) {
          errorMessage = backendMessage.trim()
        } else {
          errorMessage = 'A imagem não foi reconhecida como um certificado de fornecedor.'
        }
      } else if (error.response?.status === 413) {
        errorMessage = 'Arquivo muito grande. Tente com uma imagem menor.'
      } else if (error.response?.status >= 500) {
        errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.'
      } else {
        errorMessage = error.response?.data?.message || 'Por favor, tente novamente.'
      }
      
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: errorMessage,
      })
      handleRemove()
    })
    .finally(() => {
      setLoading(false)
    })
  }

  const handleFileSelect = async (file: File) => {
    console.log('Arquivo selecionado:', {
      name: file.name,
      size: file.size,
      type: file.type
    })
    
    if (!file) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Nenhum arquivo selecionado.',
      })
      return
    }
    
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Arquivo muito grande. O tamanho máximo é 10MB.',
      })
      return
    }
    
    if (!(file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg')) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Somente arquivos JPEG ou PNG podem ser carregados.',
      })
      return
    }
    
    setCertificate(prev => ({
      ...prev,
      supplierFile: file
    }))
    sendFileToOCR(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    
    const file = e.dataTransfer.files[0]
    handleFileSelect(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(true)
  }

  const handleDragLeave = () => {
    setIsDragActive(false)
  }

  const handleClick = () => {
    setIsSourceModalOpen(true)
  }

  const handleChooseUpload = () => {
    setIsSourceModalOpen(false)
    fileInputRef.current?.click()
  }

  const handleChooseGallery = () => {
    setIsSourceModalOpen(false)
    setIsGalleryModalOpen(true)
  }

  const handleSelectFromGallery = async (selectedFiles: GalleryFile[]) => {
    if (!selectedFiles || selectedFiles.length === 0) return
    const selected = selectedFiles[0]
    setIsGalleryModalOpen(false)

    try {
      setLoading(true)
      const targetUrl = selected.signedUrl || selected.fileUrl
      const response = await fetch(targetUrl)
      const blob = await response.blob()
      const fallbackName = selected.fileName || selected.originalName || selected.title || 'certificado.png'
      const file = new File([blob], fallbackName, { type: blob.type || selected.mimeType || 'image/png' })
      setCertificate((prev: any) => ({
        ...prev,
        supplierFile: file,
      }))
      sendFileToOCR(file)
    } catch (err) {
      console.error(err)
      setCertificate((prev: any) => ({
        ...prev,
        supplierFile: selected.fileUrl,
      }))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  useEffect(() => {
    setImageError(false)
    if (certificate.supplierFile instanceof File) {
      const objUrl = URL.createObjectURL(certificate.supplierFile)
      setDisplayUrl(objUrl)
      return () => {
        URL.revokeObjectURL(objUrl)
      }
    }

    if (typeof certificate.supplierFile === 'string' && certificate.supplierFile.trim()) {
      const fileUrl = certificate.supplierFile.trim()
      if (
        fileUrl.startsWith('blob:') ||
        fileUrl.startsWith('data:') ||
        fileUrl.includes('X-Goog-Signature')
      ) {
        setDisplayUrl(fileUrl)
        return
      }

      let isMounted = true
      setIsLoading(true)
      AttachmentApiService.getSignedUrl(fileUrl)
        .then((url) => {
          if (isMounted) {
            setImageError(false)
            setDisplayUrl(url || fileUrl)
          }
        })
        .catch(() => {
          if (isMounted) {
            setDisplayUrl(fileUrl)
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false)
          }
        })

      return () => {
        isMounted = false
      }
    }

    setDisplayUrl('')
  }, [certificate.supplierFile])

  const handleRemove = () => {
    setImageError(false)
    setDisplayUrl('')
    setCertificate((prev: any) => ({
      ...prev,
      supplierFile: null
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImageClick = () => {
    if (!imageError && displayUrl) {
      setIsFullScreenOpen(true)
    }
  }

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false)
  }

  const hasFile = certificate.supplierFile ? true : false
  const fileName = certificate.supplierFile instanceof File
    ? certificate.supplierFile.name
    : typeof certificate.supplierFile === 'string'
    ? certificate.supplierFile.split('?')[0].split('#')[0].split('/').pop() || 'Certificado de origem'
    : 'Certificado de origem original'

  const getFileSize = () => {
    if (certificate.supplierFile instanceof File) {
      return `${(certificate.supplierFile.size / 1024 / 1024).toFixed(1)} MB`
    }
    return '-'
  }
  
  return(
    <>
      <UploadContainer>
        {!hasFile ? (
          <UploadArea
            isDragActive={isDragActive}
            hasFile={hasFile}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleChange}
            />
            
            <UploadContent>
              <UploadIcon>
                <FileUp />
              </UploadIcon>
              <UploadText>
                Certificado do fornecedor
                <br />
                Somente JPEG ou PNG
              </UploadText>
            </UploadContent>
          </UploadArea>
        ) : (
          <FilePreviewContainer>
            <RemoveButton onClick={handleRemove}>
              <X size={18} strokeWidth={3} />
            </RemoveButton>
            {imageError ? (
              <div
                style={{
                  width: '100%',
                  height: '12rem',
                  borderRadius: '1rem',
                  backgroundColor: '#F3F4F6',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  color: '#9CA3AF',
                  padding: '1rem',
                  textAlign: 'center',
                }}
              >
                <ImageIcon size={32} />
                <span style={{ fontSize: '0.813rem', color: '#6B7280' }}>
                  Arquivo indisponível no storage
                </span>
              </div>
            ) : isLoading || !displayUrl ? (
              <div
                style={{
                  width: '100%',
                  height: '12rem',
                  borderRadius: '1rem',
                  backgroundColor: '#F9FAFB',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  color: '#9CA3AF',
                  padding: '1rem',
                }}
              >
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '0.813rem', color: '#6B7280' }}>
                  Carregando arquivo...
                </span>
              </div>
            ) : (
              <FilePreviewImage 
                draggable={false} 
                src={displayUrl} 
                alt={fileName}
                onClick={handleImageClick}
                onError={() => {
                  if (displayUrl) {
                    setImageError(true)
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            )}
            <FilePreviewInfo>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <ImageIcon size={18} />
                <span>{getFileSize()}</span>
              </div>
              <div style={{fontWeight: '500', color: '#374151', wordBreak: 'break-all'}}>
                {fileName}
              </div>
            </FilePreviewInfo>
          </FilePreviewContainer>
        )}
      </UploadContainer>

      {isFullScreenOpen && !imageError && displayUrl && (
        <FullScreenModal onClick={handleCloseFullScreen}>
          <CloseButton onClick={handleCloseFullScreen}>
            <X size={24} />
          </CloseButton>
          <FullScreenImage 
            src={displayUrl} 
            alt={fileName}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        </FullScreenModal>
      )}

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        onChooseUpload={handleChooseUpload}
        onChooseGallery={handleChooseGallery}
        title="Certificado do Fornecedor"
        description="Selecione se deseja enviar um novo arquivo do seu dispositivo ou escolher um anexo existente na galeria da empresa."
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onSelectFiles={handleSelectFromGallery}
        multiple={false}
        defaultFilter="image"
        title="Galeria da Empresa - Certificado do Fornecedor"
      />
    </>
  )
}

export default SupplierFileComponent
